import { ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Input,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import { redirect, useLoaderData, useNavigate } from "react-router-dom";
import { api } from "services/api.service";
import { OnboardingStage } from "types/onboarding";
import { formatNumberToCurrency } from "utils/helpers";
import * as yup from "yup";

interface LoaderData {
  earningsBalance: number;
  onboardingStages: OnboardingStage[];
}

export const loader = async () => {
  try {
    const { data: earnings } = await api.get("/earnings");
    const { data: onboarding } = await api.get("/onboarding");

    const earningsBalance = earnings.earningsBalance / 100.0;

    const onboardingStages = Array.isArray(onboarding) ? onboarding : Object.values(onboarding);
    const onboardingPaymentInfo = onboardingStages.find((stage) => stage.name === "payment_info");
    if (!onboardingPaymentInfo || !onboardingPaymentInfo.completed) return redirect("../");

    return { earningsBalance };
  } catch (error) {
    console.error("Error fetching data:", error);
    return null;
  }
};

export default function EarningsWithdrawalPage() {
  const { earningsBalance } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const [modalStep, setModalStep] = useState(0);
  const [withdrawalAmount, setWithdrawalAmount] = useState(0);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);

  const validationSchema = yup
    .number()
    .required()
    .min(1, "Minimum withdraw amount is $1.00.")
    .max(earningsBalance, "Withdraw amount cannot be greater than earnings balance.")
    .transform((value) => Math.round(value * 100) / 100);

  const formattedEarningsBalance = useMemo(() => {
    return formatNumberToCurrency(earningsBalance);
  }, [earningsBalance]);

  const formattedWithdrawalAmount = useMemo(() => {
    return formatNumberToCurrency(withdrawalAmount);
  }, [withdrawalAmount]);

  const formattedRemainingEarningsBalance = useMemo(() => {
    return formatNumberToCurrency(earningsBalance - withdrawalAmount);
  }, [earningsBalance, withdrawalAmount]);

  const handleReset = useCallback(() => {
    setModalStep(0);
    setWithdrawalAmount(0);
    setErrors({});
  }, []);

  const handleValidate = useCallback(async () => {
    const newErrors = {};
    setLoading(true);

    try {
      const validatedData = await validationSchema.validate(withdrawalAmount);
      setWithdrawalAmount(validatedData);
      setModalStep(1);
    } catch (error) {
      if (error instanceof yup.ValidationError && typeof error.path === "string") {
        (newErrors as Record<string, string[]>)[error.path] = [error.message];
      }
    } finally {
      setErrors(newErrors);
      setLoading(false);
    }
  }, [validationSchema, withdrawalAmount]);

  const ErrorAlert = () => (
    <>
      {errors && Object.keys(errors).length > 0 && (
        <Alert severity="error">{Object.values(errors).flat().join(", ")}</Alert>
      )}
    </>
  );

  const handleSubmit = useCallback(async () => {
    const newErrors = {};
    setLoading(true);

    try {
      await api.post("/earnings/withdraw", { withdrawalAmount }).then(() => setModalStep(2));
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data.error;
        (newErrors as Record<string, string[]>)[errorMessage] = [errorMessage];
      } else {
        const errorMessage = error instanceof Error ? error.message : String(error);
        (newErrors as Record<string, string[]>)[errorMessage] = [errorMessage];
      }
    } finally {
      setErrors(newErrors);
      setLoading(false);
    }
  }, [withdrawalAmount]);

  return (
    <Dialog open={true} onClose={() => navigate(-1)} fullWidth maxWidth="sm">
      {modalStep === 0 && (
        <>
          <DialogTitle>Enter earnings withdraw amount</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Available to withdraw
              <Typography fontSize={24} fontWeight={600}>
                {formattedEarningsBalance}
              </Typography>
            </DialogContentText>
            <Typography marginTop={2}>Withdraw amount</Typography>
            <Input
              autoFocus
              fullWidth
              required
              margin="dense"
              type="number"
              inputProps={{
                step: 0.01,
              }}
              name="withdrawalAmount"
              size="small"
              placeholder="Enter the amount to withdraw"
              onChange={(e) => setWithdrawalAmount(Number(e.target.value))}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
            />
            <ErrorAlert />
          </DialogContent>
          <DialogActions>
            <Button disabled={loading} fullWidth variant="contained" onClick={handleValidate}>
              Continue
            </Button>
          </DialogActions>
        </>
      )}
      {modalStep === 1 && (
        <>
          <DialogTitle>Review</DialogTitle>
          <Button
            startIcon={<ArrowBack />}
            disabled={loading}
            onClick={handleReset}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            Back
          </Button>
          <DialogContent>
            <DialogContentText>You are about to submit a withdrawal:</DialogContentText>
            <Divider sx={{ marginY: 1 }} />
            <Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Earnings balance</Typography>
                <Typography>{formattedEarningsBalance} USD</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Withdraw amount</Typography>
                <Typography>{formattedWithdrawalAmount} USD</Typography>
              </Stack>
            </Stack>
            <Divider sx={{ marginY: 1 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight={600}>New earnings balance</Typography>
              <Typography fontWeight={600}>{formattedRemainingEarningsBalance} USD</Typography>
            </Stack>
          </DialogContent>
          <ErrorAlert />
          <DialogActions>
            <Button fullWidth disabled={loading} variant="contained" onClick={handleSubmit}>
              Submit withdrawal request
            </Button>
          </DialogActions>
        </>
      )}
      {modalStep === 2 && (
        <>
          <DialogTitle variant="h6">Withdrawal request submitted!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              A confirmation email will be sent to you with the details of your withdrawal request.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button disabled={loading} variant="contained" onClick={() => navigate(-1)}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
